// src/services/apiService.js
import axiosConfig from "../config/axiosConfig";
import { AUTH_ENDPOINTS, DUBBING_ENDPOINTS } from "../utils/endpoints";

class ApiService {
  constructor() {
    this.axiosInstance = axiosConfig.getInstance();
    this.isAuthenticating = false;
  }

  // Token management methods (delegated to axiosConfig)
  getAuthToken() {
    return axiosConfig.getAuthToken();
  }

  setAuthToken(token) {
    return axiosConfig.setAuthToken(token);
  }

  removeAuthToken() {
    axiosConfig.removeAuthToken();
    this.isAuthenticating = false;
  }

  isTokenExpired(token = null) {
    return axiosConfig.isTokenExpired(token);
  }

  isAuthenticated() {
    return axiosConfig.isAuthenticated();
  }

  // Additional token utility methods
  isTokenExpiringSoon(minutesThreshold = 5) {
    return axiosConfig.isTokenExpiringSoon(null, minutesThreshold);
  }

  getTokenExpirationTime() {
    return axiosConfig.getTokenExpirationTime();
  }

  getTimeUntilExpiration() {
    return axiosConfig.getTimeUntilExpiration();
  }

  // ==================== AUTH API METHODS ====================

  /**
   * Universal authentication - handles both sign-in and sign-up
   * POST /api/auth/auth
   */
  async sendMagicLink(email) {
    try {
      const response = await this.axiosInstance.post(AUTH_ENDPOINTS.auth, {
        email,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Legacy sign-in method (for backward compatibility)
   * POST /api/auth/login
   */
  async sendMagicLinkLogin(email) {
    try {
      const response = await this.axiosInstance.post(AUTH_ENDPOINTS.login, {
        email,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Legacy sign-up method (for backward compatibility)
   * POST /api/auth/signup
   */
  async sendMagicLinkSignup(email) {
    try {
      const response = await this.axiosInstance.post(AUTH_ENDPOINTS.signup, {
        email,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Authenticate with magic link token
   * GET /api/auth/magic-link/{token}
   */
  async authenticateWithMagicLink(magicToken) {
    try {
      const response = await this.axiosInstance.get(
        `/api/auth/magic-link/${magicToken}`
      );
      const data = response.data;

      // Validate response structure
      if (!data.access_token || !data.user) {
        throw new Error("Invalid authentication response from server");
      }

      // Save token using axiosConfig
      this.setAuthToken(data.access_token);
      return data;
    } catch (error) {
      console.error(
        "Magic link authentication failed:",
        error.response?.status,
        error.message
      );

      // Handle specific authentication errors
      if (error.response?.status === 401) {
        throw new Error("This magic link has expired or already been used.");
      } else if (error.response?.status === 404) {
        throw new Error("Invalid magic link. Please request a new one.");
      }
      throw error;
    }
  }

  /**
   * Get current user information
   * GET /api/auth/me
   */
  async getCurrentUser() {
    try {
      // Check token validity before making request
      if (this.isTokenExpired()) {
        this.removeAuthToken();
        throw new Error("Your session has expired. Please log in again.");
      }

      const response = await this.axiosInstance.get(AUTH_ENDPOINTS.me);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Admin: Create new user
   * POST /api/auth/admin/users
   */
  async createUser(email, usageLimit) {
    try {
      const response = await this.axiosInstance.post(
        AUTH_ENDPOINTS.createUser,
        {
          email,
          usage_limit: usageLimit,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Test endpoint: Get magic link info for email
   * GET /api/auth/test/magic-link-info/{email}
   */
  async getTestMagicLinkInfo(email) {
    try {
      const response = await this.axiosInstance.get(
        `${AUTH_ENDPOINTS.testMagicLinkInfo}/${email}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== USER API METHODS ====================

  /**
   * Get user profile
   * GET /api/users/profile
   */
  async getUserProfile() {
    try {
      const response = await this.axiosInstance.get("/api/users/profile");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user profile
   * PUT /api/users/profile
   */
  async updateUserProfile(profileData) {
    try {
      const response = await this.axiosInstance.put(
        "/api/users/profile",
        profileData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== ADMIN API METHODS ====================

  /**
   * Get all users (admin only)
   * GET /api/admin/users
   */
  async getAllUsers(page = 1, limit = 10) {
    try {
      const response = await this.axiosInstance.get("/api/admin/users", {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user (admin only)
   * PUT /api/admin/users/{id}
   */
  async updateUser(userId, userData) {
    try {
      const response = await this.axiosInstance.put(
        `/api/admin/users/${userId}`,
        userData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete user (admin only)
   * DELETE /api/admin/users/{id}
   */
  async deleteUser(userId) {
    try {
      const response = await this.axiosInstance.delete(
        `/api/admin/users/${userId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get admin approval system status
   * GET /api/admin/approval/status
   */
  async getApprovalSystemStatus() {
    try {
      const response = await this.axiosInstance.get(
        "/api/admin/approval/status"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get pending jobs for admin approval
   * GET /api/admin/jobs/pending
   */
  async getPendingJobs(page = 1, limit = 20) {
    try {
      const response = await this.axiosInstance.get("/api/admin/jobs/pending", {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get pending job details
   * GET /api/admin/jobs/pending/{job_id}
   */
  async getPendingJobDetails(jobId) {
    try {
      const response = await this.axiosInstance.get(
        `/api/admin/jobs/pending/${jobId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Review job (approve/reject)
   * POST /api/admin/jobs/{job_id}/review
   */
  async reviewJob(jobId, action, notes = "") {
    try {
      const payload = {
        action,
        notes,
      };

      // For reject action, also send 'reason' field (backend requires it)
      if (action === 'reject') {
        payload.reason = notes;
      }

      const response = await this.axiosInstance.post(
        `/api/admin/jobs/${jobId}/review`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get admin dashboard stats
   * GET /api/admin/dashboard/stats
   */
  async getAdminDashboardStats() {
    try {
      const response = await this.axiosInstance.get(
        "/api/admin/dashboard/stats"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get reviewed jobs history
   * GET /api/admin/jobs/history
   */
  async getReviewedJobsHistory(page = 1, limit = 20, actionFilter = null) {
    try {
      const params = { page, limit };
      if (actionFilter) {
        params.action_filter = actionFilter;
      }

      const response = await this.axiosInstance.get("/api/admin/jobs/history", {
        params,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== DUBBING API METHODS ====================

  /**
   * Upload video for dubbing
   * POST /api/dubbing/videos/upload
   */
  async uploadVideo(file, onUploadProgress) {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: onUploadProgress,
        timeout: 300000, // 5 minutes for upload
      };

      const response = await this.axiosInstance.post(
        DUBBING_ENDPOINTS.uploadVideo,
        formData,
        config
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create dubbing job
   * POST /api/dubbing/videos/{video_id}/dub
   */
  async createDubbingJob(videoId, params) {
    try {
      const { project_name, ...apiParams } = params;

      const response = await this.axiosInstance.post(
        `${DUBBING_ENDPOINTS.createDubbingJob}/${videoId}/dub`,
        apiParams
      );

      return {
        ...response.data,
        project_name: project_name || `Job ${response.data.job_id}`,
        video_filename: params.video_filename || "Unknown",
      };
    } catch (error) {
      console.error("Create dubbing job error:", error);
      throw error;
    }
  }

  /**
   * Get dubbing job status
   * GET /api/dubbing/jobs/{job_id}/status
   */
  async getJobStatus(jobId) {
    try {
      const response = await this.axiosInstance.get(
        `${DUBBING_ENDPOINTS.getJobStatus}/${jobId}/status`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * List dubbing jobs
   * GET /api/dubbing/jobs
   */
  async listDubbingJobs(page = 1, limit = 10, status = null) {
    try {
      const params = { page, limit };
      if (status) {
        params.status = status;
      }

      const response = await this.axiosInstance.get(
        DUBBING_ENDPOINTS.listJobs,
        {
          params,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * List user videos
   * GET /api/dubbing/videos
   */
  async listVideos(page = 1, limit = 10) {
    try {
      const response = await this.axiosInstance.get(
        DUBBING_ENDPOINTS.listVideos,
        {
          params: { page, limit },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete video
   * DELETE /api/dubbing/videos/{video_id}
   */
  async deleteVideo(videoId) {
    try {
      const response = await this.axiosInstance.delete(
        `${DUBBING_ENDPOINTS.deleteVideo}/${videoId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cancel dubbing job
   * POST /api/dubbing/jobs/{job_id}
   */
  async cancelDubbingJob(jobId) {
    try {
      const response = await this.axiosInstance.post(
        `${DUBBING_ENDPOINTS.cancelJob.replace("{{ID}}", jobId)}`
      );
      return response.data;
    } catch (error) {
      console.error("Cancel job API error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      // Improve error message for user
      if (error.response?.data?.detail) {
        const enhancedError = new Error(error.response.data.detail);
        enhancedError.response = error.response;
        throw enhancedError;
      }
      throw error;
    }
  }

  /**
   * Delete dubbing job
   * DELETE /api/dubbing/jobs/{job_id}
   */
  async deleteDubbingJob(jobId) {
    try {
      const response = await this.axiosInstance.delete(
        `${DUBBING_ENDPOINTS.deleteJob}/${jobId}`
      );
      return response.data;
    } catch (error) {
      console.error("Delete job API error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      // Improve error message for user
      if (error.response?.data?.detail) {
        const enhancedError = new Error(error.response.data.detail);
        enhancedError.response = error.response;
        throw enhancedError;
      }
      throw error;
    }
  }

  /**
   * Get supported languages
   * GET /api/dubbing/languages
   */
  async getSupportedLanguages() {
    try {
      const response = await this.axiosInstance.get(
        DUBBING_ENDPOINTS.getLanguages
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user dubbing statistics
   * GET /api/dubbing/stats
   */
  async getUserDubbingStats() {
    try {
      const response = await this.axiosInstance.get(
        DUBBING_ENDPOINTS.getUserStats
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get system status
   * GET /api/dubbing/system/status
   */
  async getSystemStatus() {
    try {
      const response = await this.axiosInstance.get(
        DUBBING_ENDPOINTS.getSystemStatus
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Submit contact form
   * POST /api/contact/
   */
  async submitContactForm(formData) {
    try {
      const response = await this.axiosInstance.post(
        DUBBING_ENDPOINTS.submitContactForm,
        formData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Health check
   * GET /api/health
   */
  async healthCheck() {
    try {
      const response = await this.axiosInstance.get("/api/health");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get API version
   * GET /api/version
   */
  async getVersion() {
    try {
      const response = await this.axiosInstance.get("/api/version");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get video upload limits
   * GET /api/dubbing/limits
   */
  async getVideoLimits() {
    try {
      const response = await this.axiosInstance.get("/api/dubbing/limits");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== AUDIO TRANSLATION API METHODS ====================

  /**
   * Validate YouTube URL
   * POST /api/audio/youtube/validate
   */
  async validateYouTubeUrl(youtubeUrl) {
    try {
      const response = await this.axiosInstance.post("/api/audio/youtube/validate", {
        youtube_url: youtubeUrl
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create audio translation job
   * POST /api/audio/translate
   */
  async createAudioTranslation(jobData) {
    try {
      const response = await this.axiosInstance.post("/api/audio/translate", jobData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get audio translation jobs
   * GET /api/audio/jobs
   */
  async getAudioJobs(status = null, limit = 50, skip = 0) {
    try {
      const params = { limit, skip };
      if (status) params.status = status;
      
      const response = await this.axiosInstance.get("/api/audio/jobs", { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get specific audio job
   * GET /api/audio/jobs/{job_id}
   */
  async getAudioJob(jobId) {
    try {
      const response = await this.axiosInstance.get(`/api/audio/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete audio job
   * DELETE /api/audio/jobs/{job_id}
   */
  async deleteAudioJob(jobId) {
    try {
      const response = await this.axiosInstance.delete(`/api/audio/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cancel audio job
   * POST /api/audio/jobs/{job_id}/cancel
   */
  async cancelAudioJob(jobId) {
    try {
      const response = await this.axiosInstance.post(`/api/audio/jobs/${jobId}/cancel`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Upload video for audio translation
   * POST /api/audio/upload/video
   */
  async uploadVideoForTranslation(formData, onProgress) {
    try {
      const response = await this.axiosInstance.post("/api/audio/upload/video", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create audio translation from uploaded video
   * POST /api/audio/translate/upload
   */
  async createAudioTranslationFromUpload(data) {
    try {
      const response = await this.axiosInstance.post("/api/audio/translate/upload", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get audio job chunks
   * GET /api/audio/jobs/{job_id}/chunks
   */
  async getAudioJobChunks(jobId) {
    try {
      const response = await this.axiosInstance.get(`/api/audio/jobs/${jobId}/chunks`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get specific chunk details
   * GET /api/audio/chunks/{chunk_id}
   */
  async getAudioChunk(chunkId) {
    try {
      const response = await this.axiosInstance.get(`/api/audio/chunks/${chunkId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retry a specific chunk
   * POST /api/audio/chunks/{chunk_id}/retry
   */
  async retryAudioChunk(chunkId) {
    try {
      const response = await this.axiosInstance.post(`/api/audio/chunks/${chunkId}/retry`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retry all failed chunks in a job
   * POST /api/audio/jobs/{job_id}/retry-all-failed-chunks
   */
  async retryAllFailedChunks(jobId) {
    try {
      const response = await this.axiosInstance.post(`/api/audio/jobs/${jobId}/retry-all-failed-chunks`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export { apiService };
export default apiService;
